import React, { useContext, useState } from 'react';
import { storeSVC } from '@com/core/common/services/api';
import { reportEvent } from '@com/core/common/utils/sentry';
import { TStore } from '@com/core/common/types/catalog';
import { TPostStoreFollowingListResponse } from '@com/core/common/services/store';
import { AppContext } from './appContext';

type TProps = {
    children: JSX.Element;
    store: TStore;
};

interface IState {
    isFollowed: boolean;
    isFollowedLoading: boolean;
    followingStoresList: TPostStoreFollowingListResponse['data'];
}

interface IContext extends IState {
    followStore: () => Promise<void>;
    unfollowStore: () => Promise<void>;
    fetchStoreFollowing: ({ page, perPage }: { page: number; perPage: number }) => Promise<void>;
    storeCode: TStore['storeCode'];
    storeBanner: string;
    storeLogo: string;
    storeName: TStore['settings']['name'];
    storeUrl: TStore['storeUrl'];
    storeNav: TStore['nav'];
}

const initContext: IContext = {
    followStore: () => null,
    unfollowStore: () => null,
    fetchStoreFollowing: () => null,
    storeCode: '',
    storeBanner: '',
    storeLogo: '',
    storeName: '',
    storeUrl: '',
    storeNav: [],
    isFollowed: false,
    isFollowedLoading: false,
    followingStoresList: null,
};

export const StoreContext = React.createContext<IContext>(initContext);

export const StoreContextProvider = ({ children, store }: TProps): React.ReactElement => {
    const { getCdnPath, locale } = useContext(AppContext);

    const [isFollowed, setIsFollowed] = useState(Boolean(store?.isFollowed));
    const [isFollowedLoading, setIsFollowedLoading] = useState(false);
    const [followingStoresList, setFollowingStoresList] = useState<TPostStoreFollowingListResponse['data']>(null);

    const headerBannerPath = store?.settings?.header_banner || '';
    const logoPath = store?.settings?.logo || '';

    const storeCode = store?.storeCode || '';
    const storeBanner = headerBannerPath ? getCdnPath(headerBannerPath) : '';
    const storeLogo = logoPath ? getCdnPath(logoPath) : '';
    const storeName = store?.settings?.name || '';
    const storeUrl = store?.storeUrl || '';
    const storeNav = store?.nav || [];

    const followStore = async (code: string = storeCode): Promise<void> => {
        setIsFollowedLoading(true);
        try {
            const response = await storeSVC(locale.apiLocale).postStoreFollowingCreate({
                storeCode: code,
                isActive: 1,
            });
            if (response.status === 200) {
                setIsFollowed(true);
            }
            setIsFollowedLoading(false);
        } catch (error) {
            reportEvent('Error following store', 'storeContext.followStore', 'error', { error });
            setIsFollowedLoading(false);
        }
    };

    const unfollowStore = async (code: string = storeCode): Promise<void> => {
        setIsFollowedLoading(true);
        try {
            const response = await storeSVC(locale.apiLocale).postStoreFollowingCreate({
                storeCode: code,
                isActive: 0,
            });
            if (response.status === 200) {
                setIsFollowed(false);
            }
            setIsFollowedLoading(false);
        } catch (error) {
            reportEvent('Error unfollowing store', 'storeContext.followStore', 'error', { error });
            setIsFollowedLoading(false);
        }
    };

    const fetchStoreFollowing = async (params = { page: 1, perPage: 20 }) => {
        try {
            const response = await storeSVC(locale.apiLocale).postStoreFollowingList(params);
            setFollowingStoresList(response.data);
        } catch (error) {
            reportEvent('Error fetching list for store following', 'storeContext.fetchStoreFollowing', 'error', {
                error,
            });
        }
    };

    const contextValue = {
        followStore,
        unfollowStore,
        fetchStoreFollowing,
        storeCode,
        storeBanner,
        storeLogo,
        storeName,
        storeUrl,
        storeNav,
        isFollowed,
        isFollowedLoading,
        followingStoresList,
    };

    return <StoreContext.Provider value={contextValue}>{children}</StoreContext.Provider>;
};
